import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface ZoomableSvgDiagramProps {
    svgMarkup: string;
    caption?: string;
    zoomable?: boolean;
}

/**
 * Every id inside `svgMarkup` (defs, markers, edge/node ids) is generated by the
 * export tool from a single fixed root id, so re-namespacing that one substring
 * re-namespaces all of it consistently. Needed because the inline preview and the
 * fullscreen viewer can both be mounted at once, and the page may hold more than
 * one instance of this component.
 */
function namespaceSvgIds(svgMarkup: string, instanceId: string): string {
    return svgMarkup.replace(/graph-1/g, `graph-${instanceId}`);
}

function normalizeInjectedSvg(container: HTMLDivElement | null, mode: 'inline' | 'modal'): void {
    if (!container) return;

    const svg = container.querySelector('svg');
    if (!svg) return;

    svg.removeAttribute('height');
    svg.setAttribute('width', '100%');
    svg.style.display = 'block';

    if (mode === 'inline' || mode === 'modal') {
        svg.style.width = '100%';
        svg.style.height = '100%';
    } else {
        svg.style.width = '';
        svg.style.height = 'auto';
    }

    const styleAttr = svg.getAttribute('style');
    if (styleAttr) {
        const cleanedStyle = styleAttr
            .replace(/\bmax-width\s*:[^;]+;?/gi, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        if (cleanedStyle) {
            svg.setAttribute('style', cleanedStyle);
        } else {
            svg.removeAttribute('style');
        }
    }

    if (!svg.hasAttribute('viewBox')) {
        try {
            const bbox = svg.getBBox();
            if (Number.isFinite(bbox.x) && Number.isFinite(bbox.y) && bbox.width > 0 && bbox.height > 0) {
                const padding = 24;
                svg.setAttribute(
                    'viewBox',
                    `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`,
                );
            }
        } catch {
            // Some browsers may throw before layout; safe to leave as-is.
        }
    }
}

function MagnifierIcon() {
    return (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-5.2-5.2m0 0a7.2 7.2 0 1 0-10.18 0 7.2 7.2 0 0 0 10.18 0Z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 8v6M8 11h6" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

function ZoomInIcon() {
    return (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-5.2-5.2m0 0a7.2 7.2 0 1 0-10.18 0 7.2 7.2 0 0 0 10.18 0Z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 8v6M8 11h6" />
        </svg>
    );
}

function ZoomOutIcon() {
    return (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-5.2-5.2m0 0a7.2 7.2 0 1 0-10.18 0 7.2 7.2 0 0 0 10.18 0Z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11h6" />
        </svg>
    );
}

function FitToScreenIcon() {
    return (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
        </svg>
    );
}

export default function ZoomableSvgDiagram({ svgMarkup, caption, zoomable }: ZoomableSvgDiagramProps) {
    const instanceId = useId().replace(/[^a-zA-Z0-9]/g, '');
    const [isOpen, setIsOpen] = useState(false);
    const inlineSvgRef = useRef<HTMLDivElement | null>(null);
    const modalSvgRef = useRef<HTMLDivElement | null>(null);

    const inlineMarkup = useMemo(() => namespaceSvgIds(svgMarkup, `${instanceId}-inline`), [svgMarkup, instanceId]);
    const modalMarkup = useMemo(() => namespaceSvgIds(svgMarkup, `${instanceId}-modal`), [svgMarkup, instanceId]);

    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        if (!isOpen) return;

        document.body.style.overflow = 'hidden';
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = 'unset';
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, close]);

    useEffect(() => {
        normalizeInjectedSvg(inlineSvgRef.current, 'inline');
    }, [inlineMarkup]);

    useEffect(() => {
        if (!isOpen) return;
        normalizeInjectedSvg(modalSvgRef.current, 'modal');
    }, [isOpen, modalMarkup]);

    return (
        <div className="my-6 rounded-r-lg border-l-4 border-darkBlue bg-gray-50 p-6 dark:border-lightBlue dark:bg-gray-800/30">
            {zoomable && <p className="mb-2 text-sm text-red">Click the image or the magnifying icon for an expanded view.</p>}
            <div
                className={`relative rounded bg-white p-3 ${zoomable ? 'cursor-pointer' : ''}`}
                role={zoomable ? 'button' : undefined}
                tabIndex={zoomable ? 0 : undefined}
                aria-label={zoomable ? 'Expand diagram to full screen' : undefined}
                onClick={zoomable ? () => setIsOpen(true) : undefined}
                onKeyDown={
                    zoomable
                        ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setIsOpen(true);
                              }
                          }
                        : undefined
                }
            >
                <div
                    ref={inlineSvgRef}
                    className="mx-auto h-[220px] md:h-[600px] [&>svg]:mx-auto [&>svg]:block [&>svg]:h-full [&>svg]:w-full [&>svg]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: inlineMarkup }}
                />
                {zoomable && (
                    <div
                        aria-hidden="true"
                        className="absolute right-3 top-3 rounded-full bg-darkBlue/90 p-2 text-white shadow-md transition-colors dark:bg-lightBlue/90"
                    >
                        <MagnifierIcon />
                    </div>
                )}
            </div>
            {caption && <div className="mt-3 text-center text-sm italic text-darkBlue dark:text-lightBlue">{caption}</div>}

            {zoomable &&
                ReactDOM.createPortal(
                    <AnimatePresence>
                        {isOpen && (
                            <>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onClick={close}
                                    className="fixed inset-0 bg-black/80"
                                    style={{ zIndex: 999999 }}
                                />
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    onClick={close}
                                    className="fixed inset-0 flex items-center justify-center p-4"
                                    style={{ zIndex: 999999 }}
                                >
                                    <div
                                        className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-lg bg-white p-6 shadow-2xl"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <div className="mb-3 flex items-center justify-between gap-4">
                                            <p className="text-sm text-red">Scroll, pinch, or use the buttons to zoom in and out.</p>
                                            <button
                                                type="button"
                                                onClick={close}
                                                aria-label="Close diagram"
                                                className="flex-shrink-0 rounded-full bg-darkBlue p-2 text-white shadow-md hover:bg-opacity-90"
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                        <div className="relative h-[220px] w-[min(1400px,calc(90vw-3rem))] overflow-hidden md:h-[600px]">
                                            <TransformWrapper
                                                initialScale={1.2}
                                                minScale={1}
                                                maxScale={6}
                                                centerOnInit={true}
                                                 onInit={(ref) => {
                                                     ref.setTransform(ref.state.positionX, -100, 1.7, 0);
                                                 }}
                                                doubleClick={{ mode: 'zoomIn' }}
                                            >
                                                {({ zoomIn, zoomOut, resetTransform }) => (
                                                    <>
                                                        <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => zoomIn()}
                                                                aria-label="Zoom in"
                                                                className="rounded-full bg-darkBlue p-2 text-white shadow-md hover:bg-opacity-90"
                                                            >
                                                                <ZoomInIcon />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => zoomOut()}
                                                                aria-label="Zoom out"
                                                                className="rounded-full bg-darkBlue p-2 text-white shadow-md hover:bg-opacity-90"
                                                            >
                                                                <ZoomOutIcon />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => resetTransform()}
                                                                aria-label="Reset zoom"
                                                                className="rounded-full bg-darkBlue p-2 text-white shadow-md hover:bg-opacity-90"
                                                            >
                                                                <FitToScreenIcon />
                                                            </button>
                                                        </div>
                                                        <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%' }}>
                                                            <div
                                                                ref={modalSvgRef}
                                                                className="h-full w-full [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
                                                                dangerouslySetInnerHTML={{ __html: modalMarkup }}
                                                            />
                                                        </TransformComponent>
                                                    </>
                                                )}
                                            </TransformWrapper>
                                        </div>
                                    </div>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>,
                    document.body,
                )}
        </div>
    );
}
